import axios from 'axios'
import { onBeforeUnmount, ref, shallowRef, watch } from 'vue'

/**
 * RED-386: "Mis läheb mootorile" for an unsaved checklist draft.
 *
 * Posts the draft to `admin.report-qc.checklist.preview` (no vendor call) a
 * moment after the last change and keeps the newest answer. The server builds
 * everything: prompts, the automatic sentence, rounds, skipped questions,
 * worst case and the save-rule problems, so nothing here re-implements the
 * engine. A slower, older answer never overwrites a newer one.
 *
 * @param {() => object|null} draft the payload (any of `items`, `queries`, `frame`), or null to send nothing
 * @param {{ delay?: number }} [options]
 */
export function useChecklistPreview(draft, { delay = 350 } = {}) {
  const preview = shallowRef(null)
  const loading = ref(false)
  const failed = ref(false)

  let timer = null
  let controller = null
  let sequence = 0

  const load = async () => {
    const mine = ++sequence
    controller?.abort()
    controller = new AbortController()
    loading.value = true

    try {
      const response = await axios.post(route('admin.report-qc.checklist.preview'), draft(), { signal: controller.signal })

      if (mine === sequence) {
        preview.value = response.data
        failed.value = false
      }
    } catch (error) {
      if (mine === sequence && error?.name !== 'CanceledError') {
        failed.value = true
      }
    } finally {
      if (mine === sequence) {
        loading.value = false
      }
    }
  }

  const schedule = () => {
    clearTimeout(timer)

    // A null draft means "not now" (the panel is closed): nothing is sent.
    if (draft() === null) {
      loading.value = false

      return
    }

    loading.value = true
    timer = setTimeout(load, delay)
  }

  watch(() => JSON.stringify(draft()), schedule, { immediate: true })

  onBeforeUnmount(() => {
    clearTimeout(timer)
    controller?.abort()
  })

  return { preview, loading, failed, reload: load }
}
